import React, { useState, useEffect, useRef } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"
import FocusGrow from "./FocusGrow"

const Card = ({ card, idx }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const cardRef = useRef(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (!isMobile) return
    const checkPosition = () => {
      if (!cardRef.current) return
      const rect = cardRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const cardCenterY = rect.top + rect.height / 2
      const screenCenterY = windowHeight / 2
      const distanceFromCenter =
        Math.abs(cardCenterY - screenCenterY) / windowHeight
      setIsExpanded(distanceFromCenter < 0.2)
    }
    checkPosition()
    window.addEventListener("scroll", checkPosition)
    return () => window.removeEventListener("scroll", checkPosition)
  }, [isMobile])

  return (
    <div
      ref={cardRef}
      className="relative flex flex-col col-span-1 h-auto bg-theme_white text-text rounded-[24px] shadow-custom_card overflow-hidden hover:cursor-pointer"
      style={{
        paddingBottom: "100%",
      }}
    >
      {/* Background layer with hover effect */}
      <div
        className="absolute inset-0 z-0"
        onMouseEnter={() => !isMobile && setIsExpanded(true)}
        onMouseLeave={() => !isMobile && setIsExpanded(false)}
      >
        <FocusGrow
          radiusMultiplier={2}
          radiusOffset={{ x: -30, y: 0 }}
          growClass={"bg-primary_lt z-0"}
          className="absolute top-[54px] lg:top-[76px] right-[64px] lg:right-[92px]"
          isExpandedExternal={isExpanded}
        >
          <div className="w-[72px] lg:w-[100px] h-[72px] lg:h-[100px]" />
        </FocusGrow>
      </div>

      {/* Content layer */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-[18px] lg:top-6 right-[38px] lg:right-[38px]">
          <img
            src={card.icon}
            alt={card.title}
            className="w-[176px] lg:w-[237px] h-auto object-cover"
          />
        </div>

        <div className="absolute bottom-0 flex flex-col p-7 lg:p-10">
          <Typography 
            variant="body_24_accent" 
            className="text-text !text-[18px] lg:!text-[20px] xl:!text-[24px] tracking-custom_wide_1"
          >
            {card.title}
          </Typography>

          <Typography variant="info_16" className="text-text my-0">
            {card.description}
          </Typography>
        </div>
      </div>
    </div>
  )
}

function AboutUs() {
  return (
    <div className="relative w-full pt-5 pb-4 md:pb-[56px] xl:pb-[112px]">
      <div className="relative w-full max-w-[345px] md:max-w-[706px] lg:max-w-[976px] mx-auto">
        <CMSRender
          contentType="AquaSiteCardGroup"
          match={{ "content.id": "about_us" }}
          renderItem={(section) => (
            <div>
              <Typography variant="h1" className="text-center mb-8 md:mb-12">
                {section.title}
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-12">
                {section.cards.map((card, idx) => (
                  <Card key={idx} card={card} idx={idx} />
                ))}
              </div>
            </div>
          )}
        />
      </div>
    </div>
  )
}

export const AldooComponent = {
  id: 'AboutUs',
  component: AboutUs,
  input: {},
  meta: {
    name: 'About Us Section',
    description: 'Interactive card grid showing company values with hover effects',
    category: 'Marketing',
  },
}

export default AboutUs
